import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ShieldAlert, Upload, FileText, Trash2, ChevronDown, ChevronRight, ArrowLeft, Download } from 'lucide-react';
import './NetworkAudit.css';

const API = process.env.REACT_APP_API_URL || '/api';

const SEV_CLASS = { Critical: 'sev-critical', High: 'sev-high', Medium: 'sev-medium', Low: 'sev-low', Info: 'sev-info' };

function scoreColor(s) {
  if (s < 40) return 'var(--red)';
  if (s < 65) return 'var(--amber)';
  if (s < 80) return 'var(--amber)';
  return 'var(--green)';
}

function SevBadge({ severity }) {
  return <span className={`na-sev-badge ${SEV_CLASS[severity] || 'sev-info'}`}>{severity}</span>;
}

function ScoreCircle({ score, size = 56 }) {
  const c = scoreColor(score ?? 0);
  return (
    <div className="na-card-score" style={{ borderColor: c, color: c, width: size, height: size, fontSize: size * 0.32 }}>
      {score ?? '?'}
    </div>
  );
}

// ── Finding row with expand ───────────────────────────────────────────────────

function FindingRow({ finding, idx }) {
  const [open, setOpen] = useState(false);
  const bg = idx % 2 === 0 ? '#fff' : 'var(--bg-subtle)';
  return (
    <>
      <tr style={{ background: bg, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: 12, color: 'var(--ink-2)' }}>
          <span style={{ marginRight: 6 }}>{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
          {finding.id}
        </td>
        <td style={{ padding: '10px 12px' }}><SevBadge severity={finding.severity} /></td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-2)' }}>{finding.category}</td>
        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>{finding.title}</td>
        <td style={{ padding: '10px 12px', fontSize: 11, color: 'var(--ink-3)' }}>{finding.standard}</td>
      </tr>
      {open && (
        <tr style={{ background: bg }}>
          <td colSpan={5} style={{ padding: '0 12px 16px' }}>
            <div className="na-finding-detail">
              <div>
                <div className="na-fd-label">Riesgo técnico</div>
                <div style={{ fontSize: 12 }}>{finding.risk}</div>
              </div>
              <div>
                <div className="na-fd-label">Impacto</div>
                <div style={{ fontSize: 12 }}>{finding.impact}</div>
              </div>
              <div>
                <div className="na-fd-label">Evidencia</div>
                <div className="na-fd-evidence">{finding.evidence}</div>
              </div>
              <div>
                <div className="na-fd-label">Recomendación</div>
                <div style={{ fontSize: 12 }}>{finding.recommendation}</div>
              </div>
              {finding.cli_commands && (
                <div className="na-finding-detail-full">
                  <div className="na-fd-label">Comandos CLI</div>
                  <div className="na-fd-code">{finding.cli_commands}</div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Detail view ───────────────────────────────────────────────────────────────

function AuditDetail({ auditId, token, onBack, onDelete }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sevFilter, setSevFilter] = useState('all');

  useEffect(() => {
    fetch(`${API}/network-audit/${auditId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAudit(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [auditId, token]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="na-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!audit) return <div style={{ padding: 32, color: 'var(--red)' }}>Error al cargar la auditoría.</div>;

  const findings = (audit.findings || []);
  const summary  = audit.summary || {};
  const filtered = sevFilter === 'all' ? findings : findings.filter(f => f.severity === sevFilter);
  const top10    = (summary.top10_critical || []).map(id => findings.find(f => f.id === id)).filter(Boolean);
  const qw       = (summary.quick_wins    || []).map(id => findings.find(f => f.id === id)).filter(Boolean);
  const score    = audit.hardening_score ?? 0;

  const handleDownload = () => {
    window.open(`${API}/network-audit/${auditId}/report`, '_blank');
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta auditoría?')) return;
    await fetch(`${API}/network-audit/${auditId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    onDelete();
  };

  return (
    <div className="na-page">
      <div className="na-header">
        <button className="btn-outline" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={14} /> Volver
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> Descargar HTML
          </button>
          <button className="btn-danger" onClick={handleDelete}>
            <Trash2 size={13} style={{ marginRight: 4 }} />Eliminar
          </button>
        </div>
      </div>

      <div className="na-detail-header">
        <div className="na-detail-device">{audit.device_name}</div>
        {audit.firmware && <div className="na-detail-firmware">{audit.firmware} &nbsp;·&nbsp; {new Date(audit.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</div>}
      </div>

      {/* Stat strip */}
      <div className="na-stat-strip">
        <div className="na-stat">
          <ScoreCircle score={score} size={48} />
          <div className="na-stat-label" style={{ marginTop: 4 }}>Score</div>
        </div>
        {[['Crítico', audit.critical_count, '#dc2626', '#fef2f2'], ['Alto', audit.high_count, '#ea580c', '#fff7ed'],
          ['Medio', audit.medium_count, '#d97706', '#fffbeb'], ['Bajo', audit.low_count, '#2563eb', '#eff6ff'],
          ['Info', audit.info_count, '#6b7280', '#f9fafb']].map(([label, count, color, bg]) => (
          <div key={label} className="na-stat" style={{ borderColor: color, background: bg }}>
            <div className="na-stat-num" style={{ color }}>{count || 0}</div>
            <div className="na-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Top 10 */}
      {top10.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: 'var(--ink)' }}>Top 10 Problemas Críticos</h3>
          <table className="na-findings-table">
            <thead><tr><th>ID</th><th>Severidad</th><th>Hallazgo</th><th>Impacto</th></tr></thead>
            <tbody>{top10.map(f => (
              <tr key={f.id}>
                <td style={{ fontWeight: 600, fontSize: 12 }}>{f.id}</td>
                <td><SevBadge severity={f.severity} /></td>
                <td style={{ fontSize: 13, fontWeight: 500 }}>{f.title}</td>
                <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{f.impact}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Quick wins */}
      {qw.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: 'var(--ink)' }}>Quick Wins — Alta mejora, bajo riesgo</h3>
          <table className="na-findings-table">
            <thead><tr><th>ID</th><th>Sev.</th><th>Hallazgo</th><th>Comando CLI</th></tr></thead>
            <tbody>{qw.map(f => (
              <tr key={f.id}>
                <td style={{ fontWeight: 600, fontSize: 12 }}>{f.id}</td>
                <td><SevBadge severity={f.severity} /></td>
                <td style={{ fontSize: 13 }}>{f.title}</td>
                <td><div className="na-fd-code" style={{ display: 'inline-block', padding: '4px 8px' }}>{f.cli_commands}</div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* All findings */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
            Hallazgos Detallados <span style={{ fontWeight: 400, color: 'var(--ink-2)', fontSize: 13 }}>({filtered.length})</span>
          </h3>
          <div className="na-filter-bar" style={{ margin: 0 }}>
            {['all', 'Critical', 'High', 'Medium', 'Low', 'Info'].map(s => (
              <button key={s} className={`na-filter-btn${sevFilter === s ? ' active' : ''}`} onClick={() => setSevFilter(s)}>
                {s === 'all' ? 'Todos' : s}
              </button>
            ))}
          </div>
        </div>
        <table className="na-findings-table">
          <thead><tr><th>ID</th><th>Severidad</th><th>Categoría</th><th>Hallazgo</th><th>Estándar</th></tr></thead>
          <tbody>{filtered.map((f, i) => <FindingRow key={f.id} finding={f} idx={i} />)}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Upload modal ──────────────────────────────────────────────────────────────

function UploadModal({ token, onClose, onSuccess }) {
  const [deviceName, setDeviceName] = useState('');
  const [file, setFile]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [dragOver, setDragOver]     = useState(false);
  const fileRef = useRef();

  const handleFile = (f) => {
    setFile(f);
    if (!deviceName && f) setDeviceName(f.name.replace(/\.(conf|yaml|txt)$/i, ''));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setError('Seleccioná un archivo de configuración.');
    if (!deviceName.trim()) return setError('Ingresá el nombre del dispositivo.');
    setLoading(true);
    setError('');
    const fd = new FormData();
    fd.append('config', file);
    fd.append('device_name', deviceName.trim());
    try {
      const r = await fetch(`${API}/network-audit/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error al procesar la auditoría.');
      onSuccess(data.audit);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="na-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="na-modal">
        <h3>Nueva Auditoría de Red</h3>
        {loading ? (
          <div className="na-progress">
            <div className="na-spinner" />
            <div className="na-progress-text">
              Analizando configuración con IA...<br />
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Este proceso puede tomar 30–60 segundos</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="na-form-group">
              <label>Nombre del dispositivo *</label>
              <input type="text" value={deviceName} onChange={e => setDeviceName(e.target.value)}
                placeholder="Ej: FortiGate-60F (10.0.0.101)" />
            </div>
            <div className="na-form-group">
              <label>Archivo de configuración *</label>
              <div
                className={`na-upload-box${dragOver ? ' drag-over' : ''}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                {file ? (
                  <>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                    <div className="na-upload-text" style={{ fontWeight: 600 }}>{file.name}</div>
                    <div className="na-upload-hint">{(file.size / 1024).toFixed(1)} KB · Haz clic para cambiar</div>
                  </>
                ) : (
                  <>
                    <div className="na-upload-icon">📁</div>
                    <div className="na-upload-text">Arrastrá el archivo o hacé clic para seleccionar</div>
                    <div className="na-upload-hint">.conf · .conf.yaml · .txt — hasta 10 MB</div>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".conf,.yaml,.txt,.cfg" style={{ display: 'none' }}
                  onChange={e => handleFile(e.target.files[0])} />
              </div>
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Upload size={14} /> Analizar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NetworkAudit() {
  const { token, user } = useAuth();
  const [audits, setAudits]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected]   = useState(null);

  const canUpload = user?.role === 'admin' || user?.role === 'technician' ||
    (user?.extra_roles || []).some(r => ['admin', 'technician'].includes(r));

  const loadAudits = useCallback(() => {
    setLoading(true);
    fetch(`${API}/network-audit`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setAudits(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  const handleSuccess = (audit) => {
    setShowModal(false);
    setSelected(audit.id);
    loadAudits();
  };

  if (selected) {
    return (
      <AuditDetail
        auditId={selected}
        token={token}
        onBack={() => setSelected(null)}
        onDelete={() => { setSelected(null); loadAudits(); }}
      />
    );
  }

  return (
    <div className="na-page">
      <div className="na-header">
        <div>
          <div className="na-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={20} color="#f97316" />
            Auditoría de Red
          </div>
          <div className="na-subtitle">Hardening de equipos Fortinet FortiGate — análisis IA contra CIS · NIST · OWASP</div>
        </div>
        {canUpload && (
          <button className="btn-primary" onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Upload size={14} /> Nueva auditoría
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 64 }}><div className="na-spinner" style={{ margin: '0 auto' }} /></div>
      ) : audits.length === 0 ? (
        <div className="na-empty">
          <div className="na-empty-icon">🛡️</div>
          <div className="na-empty-title">Sin auditorías aún</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Subí una configuración de FortiGate para comenzar el análisis.</div>
          {canUpload && (
            <button className="btn-primary" style={{ marginTop: 20 }} onClick={() => setShowModal(true)}>
              <Upload size={14} style={{ marginRight: 6 }} />Nueva auditoría
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Summary strip */}
          <div className="na-stat-strip">
            <div className="na-stat">
              <div className="na-stat-num">{audits.length}</div>
              <div className="na-stat-label">Auditorías</div>
            </div>
            <div className="na-stat" style={{ borderColor: 'var(--red)', background: 'var(--red-dim)' }}>
              <div className="na-stat-num" style={{ color: 'var(--red)' }}>
                {audits.reduce((a, x) => a + (x.critical_count || 0), 0)}
              </div>
              <div className="na-stat-label">Críticos totales</div>
            </div>
            <div className="na-stat" style={{ borderColor: 'var(--amber)', background: 'var(--amber-dim)' }}>
              <div className="na-stat-num" style={{ color: 'var(--amber)' }}>
                {audits.reduce((a, x) => a + (x.high_count || 0), 0)}
              </div>
              <div className="na-stat-label">Altos totales</div>
            </div>
            <div className="na-stat">
              <div className="na-stat-num" style={{ color: scoreColor(Math.round(audits.reduce((a, x) => a + (x.hardening_score || 0), 0) / (audits.length || 1))) }}>
                {Math.round(audits.reduce((a, x) => a + (x.hardening_score || 0), 0) / (audits.length || 1))}
              </div>
              <div className="na-stat-label">Score promedio</div>
            </div>
          </div>

          <div className="na-list">
            {audits.map(a => (
              <div key={a.id} className="na-card" onClick={() => setSelected(a.id)}>
                <ScoreCircle score={a.hardening_score} />
                <div className="na-card-info">
                  <div className="na-card-device">
                    <FileText size={13} style={{ marginRight: 6, color: 'var(--ink-2)' }} />
                    {a.device_name}
                  </div>
                  <div className="na-card-meta">{a.firmware || 'FortiOS'} · {a.config_file_name || 'config'}</div>
                  <div className="na-card-badges">
                    {a.critical_count > 0 && <span className="na-sev-badge sev-critical">{a.critical_count} Críticos</span>}
                    {a.high_count     > 0 && <span className="na-sev-badge sev-high">{a.high_count} Altos</span>}
                    {a.medium_count   > 0 && <span className="na-sev-badge sev-medium">{a.medium_count} Medios</span>}
                    {a.low_count      > 0 && <span className="na-sev-badge sev-low">{a.low_count} Bajos</span>}
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{a.total_findings} hallazgos</span>
                  </div>
                </div>
                <div className="na-card-date">
                  {new Date(a.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showModal && <UploadModal token={token} onClose={() => setShowModal(false)} onSuccess={handleSuccess} />}
    </div>
  );
}
